import { useState, useCallback, useMemo, ReactNode } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Overflow from '@alltrails/shared/icons/Overflow';
import * as styles from './styles/styles.module.scss';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { defineMessages } from '@alltrails/shared/react-intl';

type MenuPopProps = {
  items: { callback: () => void; label: ReactNode }[];
  variant?: 'default' | 'circle';
};

const MENU_POP_STRINGS = defineMessages({
  MORE_OPTIONS: {
    defaultMessage: 'More options'
  }
});

const MenuPop = ({ items, variant = 'default' }: MenuPopProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    formattedDefaultMessages: { MORE_OPTIONS }
  } = useFormatMessage(MENU_POP_STRINGS);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = useCallback(
    idx => () => {
      handleClose();
      items[idx].callback();
    },
    []
  );

  const variants = useMemo(
    () => ({
      default: {
        button: (
          <IconButton
            icon={{ Component: Overflow }}
            onClick={handleClick}
            title={MORE_OPTIONS}
            testId={MORE_OPTIONS}
            variant="flat"
          />
        ),
        transformOrigin: {
          vertical: -36,
          horizontal: 156
        }
      },
      circle: {
        button: (
          <IconButton
            icon={{ Component: Overflow }}
            onClick={handleClick}
            title={MORE_OPTIONS}
            testId="menu-pop-more-options"
            variant="elevated"
          />
        ),
        transformOrigin: {
          vertical: -64,
          horizontal: 132
        }
      }
    }),
    []
  );

  const { button, transformOrigin } = variants[variant];

  return (
    <div>
      {button}
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose} transformOrigin={transformOrigin}>
        {items.map((item, idx) => (
          <MenuItem key={idx} onClick={handleMenuItemClick(idx)} classes={{ root: styles.menuItemRoot }}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default MenuPop;
